import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, LogOut, Mail, MapPin, CalendarDays } from "lucide-react";
import { RuotaMark } from "./RuotaMark";
import { GROUP_TRIPS } from "../../data/destinations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ruota_admin_token";
const SOLD_OUT_TITLES = GROUP_TRIPS.filter((t) => t.soldOut).map((t) => t.title);
const GROUP_TITLES = GROUP_TRIPS.map((t) => t.title);

const inputCls =
  "w-full border-b border-[#0D1F23]/25 bg-transparent py-3 text-base text-[#0D1F23] placeholder-[#4A5557]/50 outline-none transition-colors duration-300 focus:border-[#168A91]";

const LoginCard = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md rounded-3xl bg-white p-10 shadow-[0_20px_60px_-30px_rgba(13,31,35,0.35)]"
      >
        <div className="flex justify-center">
          <RuotaMark size={56} duration={20} />
        </div>
        <h1 className="mt-6 text-center font-display text-3xl font-light text-[#0D1F23]">
          Inquiry <span className="italic text-[#E2725B]">Dashboard</span>
        </h1>
        <p className="mt-2 text-center font-accent text-[10px] uppercase tracking-[0.3em] text-[#4A5557]">
          Private · Ruota Advisors Only
        </p>
        <div className="mt-8 space-y-6">
          <input
            data-testid="admin-input-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className={inputCls}
          />
          <input
            data-testid="admin-input-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className={inputCls}
          />
        </div>
        {error && (
          <p data-testid="admin-login-error" className="mt-4 text-sm text-red-700">
            {error}
          </p>
        )}
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={loading}
          className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-[#168A91] py-3.5 font-accent text-[11px] uppercase tracking-[0.2em] text-[#F7F0E3] transition-colors hover:bg-[#116D73] disabled:opacity-60"
        >
          {loading ? <Loader2 size={15} className="animate-spin" /> : "Sign In"}
        </button>
      </form>
    </div>
  );
};

const EnquiryCard = ({ e }) => {
  const isWaitlist = (e.destinations || []).some((d) => SOLD_OUT_TITLES.includes(d));
  const isGroup = !isWaitlist && (e.destinations || []).some((d) => GROUP_TITLES.includes(d));
  const when = e.created_at ? new Date(e.created_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "";

  return (
    <article
      data-testid={`admin-enquiry-${e.id}`}
      className="rounded-2xl border border-[#0D1F23]/10 bg-white p-6 shadow-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-2xl font-light text-[#0D1F23]">{e.name}</h3>
          <a href={`mailto:${e.email}`} className="mt-0.5 flex items-center gap-2 text-sm text-[#168A91] hover:underline">
            <Mail size={13} /> {e.email}
          </a>
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className="font-accent text-[10px] uppercase tracking-[0.2em] text-[#4A5557]">{when}</span>
          {isWaitlist && (
            <span className="rounded-full bg-[#E2725B] px-3 py-1 font-accent text-[10px] uppercase tracking-[0.2em] text-[#0D1F23]">
              Waitlist
            </span>
          )}
          {isGroup && (
            <span className="rounded-full bg-[#168A91] px-3 py-1 font-accent text-[10px] uppercase tracking-[0.2em] text-[#F7F0E3]">
              Group Trip
            </span>
          )}
        </div>
      </div>
      {(e.destinations || []).length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <MapPin size={13} className="text-[#E2725B]" />
          {e.destinations.map((d) => (
            <span key={d} className="rounded-full border border-[#0D1F23]/15 px-3 py-1 text-xs text-[#4A5557]">
              {d}
            </span>
          ))}
        </div>
      )}
      {e.dates && (
        <p className="mt-3 flex items-center gap-2 text-sm text-[#4A5557]">
          <CalendarDays size={13} className="text-[#E2725B]" /> {e.dates}
        </p>
      )}
      {e.message && (
        <p className="mt-3 border-l-2 border-[#E2725B] pl-4 font-display text-lg italic text-[#13272B]">
          {e.message}
        </p>
      )}
    </article>
  );
};

export const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [enquiries, setEnquiries] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/enquiries`, { headers }),
      axios.get(`${API}/newsletter`, { headers }),
    ])
      .then(([eq, nl]) => {
        setEnquiries(eq.data);
        setSubscribers(nl.data);
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  const waitlistCount = enquiries.filter((e) =>
    (e.destinations || []).some((d) => SOLD_OUT_TITLES.includes(d))
  ).length;

  return (
    <main data-testid="admin-page" className="min-h-screen bg-[#F7F0E3] pb-24 pt-32">
      <div className="mx-auto max-w-5xl px-6 sm:px-10">
        <Link
          to="/"
          className="group inline-flex items-center gap-2 font-accent text-[11px] uppercase tracking-[0.25em] text-[#168A91] transition-colors hover:text-[#116D73]"
        >
          <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
          Back to site
        </Link>

        {!token && !loading && <LoginCard onLogin={setToken} />}

        {token && (
          <div data-testid="admin-dashboard">
            <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="font-accent text-xs uppercase tracking-[0.25em] text-[#168A91]">Private</p>
                <h1 className="mt-2 font-display text-4xl font-light tracking-tight text-[#0D1F23] sm:text-5xl">
                  Inquiry <span className="italic text-[#E2725B]">Dashboard</span>
                </h1>
              </div>
              <button
                data-testid="admin-logout-button"
                onClick={logout}
                className="flex items-center gap-2 rounded-full border border-[#0D1F23]/20 px-5 py-2.5 font-accent text-[10px] uppercase tracking-[0.2em] text-[#0D1F23] transition-colors hover:border-[#168A91] hover:text-[#168A91]"
              >
                <LogOut size={13} /> Sign Out
              </button>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4">
              {[
                { label: "Inquiries", value: enquiries.length, testid: "admin-stat-enquiries" },
                { label: "Waitlist", value: waitlistCount, testid: "admin-stat-waitlist" },
                { label: "Newsletter", value: subscribers.length, testid: "admin-stat-newsletter" },
              ].map((s) => (
                <div key={s.label} data-testid={s.testid} className="rounded-2xl bg-white p-5 text-center shadow-sm">
                  <p className="font-display text-4xl font-light text-[#168A91]">{s.value}</p>
                  <p className="mt-1 font-accent text-[10px] uppercase tracking-[0.25em] text-[#4A5557]">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 space-y-5">
              {enquiries.length === 0 && (
                <p className="rounded-2xl bg-white p-10 text-center font-display text-xl italic text-[#4A5557]">
                  No inquiries yet. The world is still waking up.
                </p>
              )}
              {enquiries.map((e) => (
                <EnquiryCard key={e.id} e={e} />
              ))}
            </div>

            {subscribers.length > 0 && (
              <div className="mt-14">
                <h2 className="font-display text-2xl font-light text-[#0D1F23]">
                  Postcards <span className="italic text-[#E2725B]">list</span>
                </h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  {subscribers.map((s) => (
                    <span
                      key={s.email}
                      className="rounded-full border border-[#0D1F23]/15 bg-white px-4 py-1.5 text-sm text-[#4A5557]"
                    >
                      {s.email}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
};
